/** @module Drag four corners onto the card when the detector picked the wrong outline. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface Point {
  x: number;
  y: number;
}
export type Quad = [Point, Point, Point, Point];

/** Corners as fractions of the photo, so they survive any display size. */
export const DEFAULT_QUAD: Quad = [
  { x: 0.2, y: 0.15 },
  { x: 0.8, y: 0.15 },
  { x: 0.8, y: 0.85 },
  { x: 0.2, y: 0.85 },
];

export function toPixels(quad: Quad, width: number, height: number): number[][] {
  return quad.map((point) => [Math.round(point.x * width), Math.round(point.y * height)]);
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function CornerEditor({
  photo,
  onCancel,
  onSubmit,
  busy,
}: {
  photo: string;
  onCancel: () => void;
  onSubmit: (quad: Quad, size: { width: number; height: number }) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [quad, setQuad] = useState<Quad>(DEFAULT_QUAD);
  const [dragging, setDragging] = useState<number | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const move = useCallback((index: number, clientX: number, clientY: number) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    setQuad((current) => {
      const next = [...current] as Quad;
      next[index] = {
        x: clamp((clientX - box.left) / box.width),
        y: clamp((clientY - box.top) / box.height),
      };
      return next;
    });
  }, []);

  useEffect(() => {
    if (dragging === null) return;
    const onMove = (event: PointerEvent) => {
      move(dragging, event.clientX, event.clientY);
    };
    const onUp = () => {
      setDragging(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, move]);

  const polygon = quad.map((point) => `${point.x * 100}% ${point.y * 100}%`).join(", ");

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[length:var(--text-sm)] text-(--text-muted)">{t("review.cornersHint")}</p>

      <div ref={boxRef} className="relative touch-none select-none">
        <img
          src={photo}
          alt={t("review.yourPhoto")}
          className="w-full rounded-[var(--radius-sm)]"
          onLoad={(event) => {
            setSize({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            });
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 bg-(--accent)/20"
          style={{ clipPath: `polygon(${polygon})` }}
        />
        {quad.map((point, index) => (
          <button
            key={index}
            type="button"
            aria-label={t("review.corner", { number: index + 1 })}
            onPointerDown={(event) => {
              event.preventDefault();
              setDragging(index);
            }}
            onKeyDown={(event) => {
              // Keyboard nudging keeps this usable without a pointer.
              const step = event.shiftKey ? 0.05 : 0.01;
              const moves: Record<string, [number, number]> = {
                ArrowLeft: [-step, 0],
                ArrowRight: [step, 0],
                ArrowUp: [0, -step],
                ArrowDown: [0, step],
              };
              const delta = moves[event.key];
              if (!delta) return;
              event.preventDefault();
              setQuad((current) => {
                const next = [...current] as Quad;
                next[index] = {
                  x: clamp(point.x + delta[0]),
                  y: clamp(point.y + delta[1]),
                };
                return next;
              });
            }}
            className="absolute size-11 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-(--accent) bg-(--surface)/70"
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || size.width === 0}
          onClick={() => {
            onSubmit(quad, size);
          }}
          className="min-h-(--touch) grow rounded-[var(--radius-sm)] bg-(--accent) px-4 font-medium text-(--accent-ink) disabled:opacity-50"
        >
          {t("review.recognise")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-(--touch) rounded-[var(--radius-sm)] border border-(--border-strong) px-4"
        >
          {t("review.cancel")}
        </button>
      </div>
    </div>
  );
}
